import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { loadFincenterActById, signFincenterAct, signFincenterActOo } from '../ReportsPageDucks';
import { ROLES } from '../../../_helpers/Constants';
import { ReportsPageApi } from '../ReportsPageApi';
import Notice from '../../../utils/Notice';
import { Button, Paper, Typography } from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import moment from 'moment';
import { months } from '../../../_helpers/Constants';
import { useTranslation } from 'react-i18next';

const ReportsFincenterActView = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const fincenterAct = useSelector(state => state.reportsPage.fincenterAct);
  const loading = useSelector(state => state.reportsPage.loadingFincenterAct);
  const signing = useSelector(state => state.reportsPage.signingFincenterAct);
  const user = useSelector(state => state.login.user);
  const isHead = useMemo(() => user?.roles?.includes(ROLES.ES_SERVICE_PROVIDER_HEADS), [user]);
  const isRegionEmployee = useMemo(
    () => user?.roles?.includes(ROLES.ES_REGION_MIO_EMPLOYEES),
    [user]
  );
  const isSignedByDo = fincenterAct?.signaturesDo?.length > 0;
  const isSignedByOo = fincenterAct?.signaturesOo?.length > 0;
  const isCurrentMonth = useMemo(() => {
    if (!fincenterAct?.periodMonth) return false;
    const actDate = moment(fincenterAct.periodMonth);
    const now = moment();
    return actDate.year() === now.year() && actDate.month() === now.month();
  }, [fincenterAct]);
  const canSign =
    isCurrentMonth && (
      (isHead && !isSignedByDo) ||
      (isRegionEmployee && isSignedByDo && !isSignedByOo)
    );

  const [documentUrl, setDocumentUrl] = useState(null);
  const [loadingDoc, setLoadingDoc] = useState(false);

  useEffect(() => {
    if (id) {
      dispatch(loadFincenterActById(id));
    }
  }, [id, dispatch]);

  const getFormattedDate = (date) => {
    return date ? moment(date).format('DD.MM.YYYY / HH:mm') : '';
  };

  const getFormattedMonth = (periodMonth) => {
    if (!periodMonth) return '';
    const parts = periodMonth.split('-');
    if (parts.length < 2) return periodMonth;
    const year = parts[0];
    const monthCode = parts[1];
    const monthObj = months.find(m => m.code === monthCode);
    const monthName = monthObj ? monthObj[i18n.language] : monthCode;
    return `${monthName} ${year}`;
  };

  useEffect(() => {
    const loadDocument = async () => {
      if (fincenterAct && fincenterAct.documentId) {
        try {
          setLoadingDoc(true);
          const response = await ReportsPageApi.viewFincenterAct(fincenterAct.documentId);
          // создаем URL для Blob
          const url = window.URL.createObjectURL(response.data);
          setDocumentUrl(url);
        } catch (err) {
          Notice.error('reportsPage_fincenterErrorGetActById');
        } finally {
          setLoadingDoc(false);
        }
      }
    };

    loadDocument();
    // очищаем объект URL при размонтировании
    return () => {
      if (documentUrl) {
        window.URL.revokeObjectURL(documentUrl);
      }
    };
  }, [fincenterAct]);

  if (loading) return <p>{t('reportsPage_fincenterLoadingAct')}</p>;
  if (!fincenterAct) return <p>{t('reportsPage_fincenterActNotFound')}</p>;

  return (
    <div style={{ padding: 20 }}>
      <Paper style={{ padding: '16px', marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => history.goBack()}
            style={{ marginRight: '20px', textTransform: 'none' }}
          >
            {t('reportsPage_fincenterActGoBack')}
          </Button>
          <div>
            <Typography variant="h6" component="h1" style={{ fontWeight: 'bold' }}>
              {t('reportsPage_fincenterActPage')}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {t('reportsPage_fincenterActPeriod')}: {getFormattedMonth(fincenterAct.periodMonth)} • {t('reportsPage_fincenterActCreatedDate')}: {getFormattedDate(fincenterAct.createdAt)}
            </Typography>
          </div>
        </div>
        {canSign && (
          <Button
            variant="contained"
            color="primary"
            disabled={signing}
            style={{ textTransform: 'none' }}
            onClick={() => {
              const action = isRegionEmployee ? signFincenterActOo : signFincenterAct;
              dispatch(action(fincenterAct.id, () => {
                dispatch(loadFincenterActById(fincenterAct.id));
              }));
            }}
          >
            {signing ? t('reportsPage_fincenterSigning') : t('reportsPage_fincenterSignAct')}
          </Button>
        )}
      </Paper>

      {loadingDoc ? (
        <p>{t('reportsPage_fincenterLoadingReport')}</p>
      ) : documentUrl ? (
        <div style={{ border: '1px solid #ccc', borderRadius: 4, overflow: 'hidden', height: '80vh' }}>
          <embed
            src={documentUrl}
            type="application/pdf"
            width="100%"
            height="100%"
          />
        </div>
      ) : (
        <p>{t('reportsPage_fincenterDocumentNotAvailable')}</p>
      )}
    </div>
  );
};

export default ReportsFincenterActView;